import classNames from "classnames";
import PropTypes from "prop-types";

import {
  CheckmarkCircledIcon,
  ExclamationCircledIcon,
  ExclamationTriangledIcon,
  InformationalIcon,
  MultiplyIcon,
} from "../../../icons";
import { types } from "./types";
import { TypographyComponent } from "../../typography/typography.component";
import styles from "./banner.styles.module.scss";

const getIcon = (type = types.error) => {
  let icon = <ExclamationCircledIcon />;
  if (type === types.error) {
    icon = <ExclamationCircledIcon title="exclamation" />;
  } else if (type === types.warning) {
    icon = <ExclamationTriangledIcon title="warning" />;
  } else if (type === types.success) {
    icon = <CheckmarkCircledIcon title="success" />;
  } else if (type === types.miniSuccess) {
    icon = <CheckmarkCircledIcon fillPath={"var(--gravx-color-utility-success-on-light-text)"} title="success" />;
  } else if (type === types.info) {
    icon = <InformationalIcon title="informational" />;
  }
  return icon;
};

const propTypes = {
  buttonText: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  handleButtonClick: PropTypes.func,
  handleCloseClick: PropTypes.func,
  message: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  pointerActionCursor: PropTypes.bool,
  type: PropTypes.string,
};

/**
 * banner styled per design to for snackbar messages
 * @unsupported - adding a button will currently break component
 * @unsupported - varying width sizes - set to be 720px, which will be min width
 * @param {*} props.buttonText -button text to be displayed
 * @param {*} props.handleButtonClick - action to happen when button is present and clicked
 * @param {*} props.handleCloseClick - action to happen when close icon is clicked
 * @param {*} props.message - text to display in banner
 * @param {*} props.pointerActionCursor - show a pointer on the action button for cursor
 * @param {*} props.type - specific type of banner to show
 * @returns styled banner based on type and texted passed in by props
 */
const Banner = ({
  buttonText = "",
  handleButtonClick = null,
  handleCloseClick = null,
  message = "",
  pointerActionCursor = false,
  type = types.error,
}) => {
  const hasButton = Boolean(buttonText) && Boolean(handleButtonClick);

  const getClassName = (bannerType) => {
    let className = styles.errorContainer;

    if (bannerType === types.error) {
      className = styles.errorContainer;
    } else if (bannerType === types.miniSuccess) {
      className = classNames(styles.successContainer, "mini");
    } else if (bannerType === types.success) {
      className = styles.successContainer;
    } else if (bannerType === types.warning) {
      className = styles.warningContainer;
    } else if (bannerType === types.info) {
      className = styles.infoContainer;
    }

    return className;
  };

  return (
    <div className={classNames(styles.container, hasButton && styles.containerWithBtn, getClassName(type))}>
      <div className={styles.iconContainer}>{getIcon(type)}</div>
      <div className={styles.messageContainer}>
        <TypographyComponent styling={"p2"} boldness={"medium"} color={"inherit"}>
          {message}
        </TypographyComponent>
      </div>
      {hasButton && (
        <div
          className={classNames(styles.actionButton, pointerActionCursor && styles.actionCursor)}
          onClick={(e) => handleButtonClick(e)}
          role="button"
          tabIndex={0}
          onKeyUp={(e) => handleButtonClick(e)}
        >
          {buttonText}
        </div>
      )}
      {type !== types.success && type !== types.miniSuccess && (
        <div
          className={styles.closeButton}
          onClick={(e) => handleCloseClick(e)}
          role="button"
          tabIndex={0}
          onKeyUp={(e) => handleCloseClick(e)}
          aria-label={"close"}
        >
          <MultiplyIcon />
        </div>
      )}
    </div>
  );
};

Banner.propTypes = propTypes;
export default Banner;